import React from 'react';
import { Button } from '../components/common';
import Section from '../components/common/Section';

/**
 * 首页组件
 * @returns {JSX.Element} 渲染的React组件
 */
const HomePage: React.FC = () => {
  const [activeFeatureIndex, setActiveFeatureIndex] = React.useState(0);
  const [activeMarketingIndex, setActiveMarketingIndex] = React.useState(0);

  // 自动轮播 - Core Features
  React.useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeatureIndex((prev) => (prev + 1) % 3);
    }, 5000); // 5秒切换一次

    return () => clearInterval(timer);
  }, []);

  // 自动轮播 - Marketing Features
  React.useEffect(() => {
    const timer = setInterval(() => {
      setActiveMarketingIndex((prev) => (prev + 1) % 4);
    }, 5000); // 5秒切换一次

    return () => clearInterval(timer);
  }, []);

  // 营销玩法数据
  const marketingFeatures = [
    {
      title: '优惠券',
      description: '满减、折扣、无门槛等多种优惠券，刺激客户下单转化',
      icon: '🎫',
      color: 'bg-red-50 text-red-500',
      activeColor: 'lg:bg-red-500 lg:text-white'
    },
    {
      title: '会员储值',
      description: '锁住客户消费力，提升资金回笼速度，增加客户粘性',
      icon: '💎',
      color: 'bg-purple-50 text-purple-500',
      activeColor: 'lg:bg-purple-500 lg:text-white'
    },
    {
      title: '积分商城',
      description: '消费送积分，积分换好礼，有效提升客户活跃度',
      icon: '🎁',
      color: 'bg-orange-50 text-orange-500',
      activeColor: 'lg:bg-orange-500 lg:text-white'
    },
    {
      title: '多人拼团',
      description: '老客带新客，裂变式传播，低成本快速获取新流量',
      icon: '👥',
      color: 'bg-blue-50 text-blue-500',
      activeColor: 'lg:bg-blue-500 lg:text-white'
    }
  ];

  const coreFeatures = [
    {
      title: '100+种主题颜色',
      description: '丰富多彩的主题选择，轻松匹配品牌色调，打造专属视觉体验',
      icon: '🎨',
      image: '/assets/images/colors.png'
    },
    {
      title: '多种精美首页模版',
      description: '精心设计的页面模板，适合各行各业，一键套用即可呈现专业效果',
      icon: '🧩',
      image: '/assets/images/themes.png'
    },
    {
      title: '100+高端背景图片',
      description: '内置100+张高清背景图片，为您的小程序增添专业感和吸引力',
      icon: '🖼️',
      image: '/assets/images/photos.png'
    }
  ];

  const platformFeatures = [
    { 
      name: '微信小程序', 
      icon: '/assets/images/logo/wx.png', 
      description: '10亿+月活用户，社交裂变传播快',
      bgColor: 'bg-[#07C160]/10',
      textColor: 'text-[#07C160]'
    },
    { 
      name: '支付宝小程序', 
      icon: '/assets/images/logo/alipay.png', 
      description: '商业服务生态完善，支付转化率高',
      bgColor: 'bg-[#1677FF]/10',
      textColor: 'text-[#1677FF]'
    },
    { 
      name: '抖音小程序', 
      icon: '/assets/images/logo/titok.png', 
      description: '短视频直播带货，内容变现快',
      bgColor: 'bg-[#1D1D1D]/10',
      textColor: 'text-[#1D1D1D]'
    },
    { 
      name: '小红书小程序', 
      icon: '/assets/images/logo/xhs.png', 
      description: '年轻人的生活方式平台，种草转化强',
      bgColor: 'bg-[#FF2442]/10',
      textColor: 'text-[#FF2442]'
    }
  ];

  const multiPlatformFeatures = [ 
    { 
      title: '全渠道覆盖，不错过任何顾客', 
      description: '无论顾客在微信、抖音、支付宝还是其他平台上浏览，都能顺畅访问您的店铺并完成下单，确保您的生意无处不在。', 
      icon: '🌍', 
      image: '/assets/images/themes.png',
      color: 'bg-blue-50 text-blue-600'
    }, 
    { 
      title: '统一管理，简化运营流程', 
      description: '所有平台的订单、库存、会员信息和消息在一个后台集中管理，无需频繁切换系统，大大提升运营效率。', 
      icon: '⚡', 
      image: '/assets/images/colors.png',
      color: 'bg-yellow-50 text-yellow-600'
    }, 
    { 
      title: '公域引流，私域沉淀', 
      description: '利用各大平台的流量入口吸引新顾客，同时将客户数据和关系沉淀到自有系统中，建立长期稳定的客户关系，实现持续复购。', 
      icon: '🔒', 
      image: '/assets/images/photos.png',
      color: 'bg-green-50 text-green-600'
    } 
  ];

  return (
    <>
      {/* 英雄区域*/}
      <Section
        id="hero"
        spacing="small"
        background="background"
        className="pt-4 md:pt-8 min-h-[calc(100vh-80px)] flex items-center"
      >
        <div className="flex flex-col lg:flex-row items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="lg:w-1/2 lg:pr-12 lg:mb-0 text-center lg:text-left">
            <div className="relative z-10 max-w-xl mx-auto lg:mx-0">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight scroll-fade-up">
                <span className="text-primary">佳达掌柜</span> 
                <br/>好用的店铺经营助手
              </h1>
              <div className="mt-4 md:mt-0 mb-8 md:mb-8 flex items-center text-xs sm:text-sm text-gray-500 justify-center lg:justify-start scroll-fade-up scroll-delay-2">
                <span>已有超过1000+企业选择我们的解决方案</span>
              </div>
              <div className="flex flex-wrap gap-3 md:gap-4 justify-center lg:justify-start scroll-fade-up scroll-delay-3">
                <Button size="large">
                  免费体验
                </Button>
                <Button variant="secondary" size="large">
                  了解更多
                </Button>
              </div>
            </div>
          </div>
          <div className="hidden lg:block lg:w-1/2 relative w-full lg:max-w-none mt-8 lg:mt-0">
            <div className="grid grid-cols-2 gap-4 items-center">
              <div className="relative rounded-2xl overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 scroll-fade-up bg-background">
                <img 
                  src="/assets/images/features/work.png" 
                  alt="佳达掌柜工作台" 
                  className="w-full h-auto object-contain"
                />
              </div>
              <div className="relative rounded-2xl overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 scroll-fade-up scroll-delay-1 bg-background">
                <img 
                  src="/assets/images/products/dengbaba.png" 
                  alt="佳达掌柜移动端" 
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 核心优势展示区域 */}
      <Section
        id="core-features"
        spacing="medium"
        background="white"
        className="relative overflow-hidden"
        title="千店千面，皆由你定"
        subtitle="多种设计模板与配色方案，3分钟上线高颜值小程序，让顾客一眼认出你"
      >
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-background to-transparent"></div>
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* 左侧功能列表 */}
          <div className="space-y-4">
            {coreFeatures.map((item, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl transition-all duration-300 lg:cursor-pointer ${
                  activeFeatureIndex === index 
                    ? 'lg:bg-primary/5' 
                    : 'lg:hover:bg-gray-50'
                }`}
                onClick={() => setActiveFeatureIndex(index)}
              >
                <div className="flex items-start">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl mr-4 transition-colors duration-300 ${
                    activeFeatureIndex === index ? 'lg:bg-primary lg:text-white bg-primary/10 text-primary' : 'bg-primary/10 text-primary'
                  }`}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className={`text-lg md:text-xl font-bold mb-2 transition-colors duration-300 ${
                      activeFeatureIndex === index ? 'lg:text-primary text-gray-900' : 'text-gray-900'
                    }`}>{item.title}</h3>
                    <p className="text-sm md:text-base text-gray-500">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* 右侧图片展示 */}
          <div className="hidden lg:flex relative h-[400px] w-full items-center justify-center overflow-hidden">
            {coreFeatures.map((item, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-500 ease-in-out flex items-center justify-center ${
                  activeFeatureIndex === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ))}
            
            {/* 轮播指示器 */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
              {coreFeatures.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    activeFeatureIndex === index ? 'bg-primary w-6' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  onClick={() => setActiveFeatureIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 多平台覆盖区域 */}
      <Section
        id="platforms"
        background="white"
        title="顾客在哪，店便在哪"
        subtitle="佳达掌柜支持多平台一键上线，统一管理商品、订单和会员，让流量变留量。"
        spacing="medium"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* 左侧Logo展示 */}
          {/* 左侧整合展示 */}
          <div className="hidden lg:flex relative p-4 items-center justify-between gap-4 min-h-[400px]">
            
            {/* Left: Platforms Column */}
            <div className="flex flex-col gap-4 z-10 py-4">
                {platformFeatures.map((platform, index) => (
                    <div key={index} className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-2xl flex items-center justify-center p-3 border border-gray-100 shadow-sm hover:scale-110 transition-transform duration-300 hover:z-20">
                         <img src={platform.icon} alt={platform.name} className="w-full h-full object-contain" />
                    </div>
                ))}
            </div>
            
            {/* Middle: Thick Arrow */}
            <div className="flex-1 flex flex-col items-center justify-center z-10 px-2 relative">
                {/* 粗箭头 SVG */}
                <div className="relative w-full max-w-[120px] h-20 flex items-center justify-center group">
                    <svg viewBox="0 0 100 40" className="w-full h-full drop-shadow-sm filter" preserveAspectRatio="none">
                        <defs>
                            <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#E5E7EB" stopOpacity="0.5" />
                                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.8" />
                            </linearGradient>
                        </defs>
                        <path d="M0,12 L60,12 L60,0 L100,20 L60,40 L60,28 L0,28 Z" fill="url(#arrowGradient)" className="transition-all duration-500 group-hover:fill-primary/80" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-600 bg-white/80 px-2 py-0.5 rounded backdrop-blur-sm whitespace-nowrap shadow-sm">全域整合流量</span>
                    </div>
                </div>
            </div>

            <div className="relative z-10 flex flex-col items-center gap-3">
                 <div className="relative">
                     <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full transform scale-150 animate-pulse-slow"></div>
                     <div className="w-24 h-24 sm:w-28 sm:h-28 bg-primary rounded-3xl shadow-xl flex items-center justify-center p-2 relative">
                        <img src="/assets/images/logo.jpg" alt="佳达掌柜" className="w-full h-full object-cover rounded-2xl" />
                     </div>
                 </div>
                 <div className="text-center">
                    <h3 className="font-bold text-gray-900 text-lg">佳达掌柜</h3>
                 </div>
            </div>
          </div>

          {/* 右侧价值列表 */}
          <div className="space-y-8">
            {multiPlatformFeatures.map((value, index) => (
              <div 
                key={index} 
                className="flex items-start group"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl mr-5 shrink-0 ${value.color} bg-opacity-20`}>
                  {value.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-primary transition-colors">{value.title}</h3>
                  <p className="text-gray-500 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 营销玩法区域 */}
      <Section
        id="marketing"
        spacing="medium"
        background="background"
        title="老客常来，新客不断"
        subtitle="内置多种营销玩法，让顾客主动回来，还会推荐朋友。"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* 左侧：营销功能列表（参考核心区域） */}
            <div className="space-y-4">
              {marketingFeatures.map((item, index) => (
                <div 
                  key={index}
                  className={`p-6 rounded-2xl transition-all duration-300 lg:cursor-pointer ${
                    activeMarketingIndex === index 
                      ? 'lg:bg-primary/5' 
                      : 'lg:hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveMarketingIndex(index)}
                >
                  <div className="flex items-start">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl mr-4 transition-colors duration-300 ${
                      activeMarketingIndex === index ? item.activeColor : ''
                    } ${item.color}`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className={`text-lg md:text-xl font-bold mb-2 transition-colors duration-300 ${
                        activeMarketingIndex === index ? 'lg:text-primary text-gray-900' : 'text-gray-900'
                      }`}>{item.title}</h3>
                      <p className="text-sm md:text-base text-gray-500">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 右侧：营销视觉展示 */}
            <div className="hidden lg:flex relative h-[400px] bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-8 items-center justify-center overflow-hidden border border-gray-100">
                {/* 装饰背景 */}
                <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 transition-colors duration-500 ${marketingFeatures[activeMarketingIndex].color.split(' ')[0].replace('bg-', 'bg-').replace('50', '200')}/20`}></div>
                <div className={`absolute bottom-0 left-0 w-64 h-64 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 transition-colors duration-500 ${marketingFeatures[activeMarketingIndex].color.split(' ')[0].replace('bg-', 'bg-').replace('50', '200')}/20`}></div>
                
                {/* 模拟手机/卡片堆叠效果 */}
                <div className="relative z-10 w-64">
                    {/* 后面的卡片 */}
                    <div className="absolute top-0 left-0 w-full h-80 bg-white rounded-2xl shadow-lg transform rotate-6 opacity-60 scale-90 translate-x-4 transition-all duration-500"></div>
                    <div className="absolute top-0 left-0 w-full h-80 bg-white rounded-2xl shadow-lg transform -rotate-6 opacity-80 scale-95 -translate-x-4 transition-all duration-500"></div>
                    
                    {/* 主卡片 - 动态展示内容 */}
                    <div className="relative w-full h-80 bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col border border-gray-50 transition-all duration-500">
                        {/* 顶部动态颜色区域 */}
                        <div className={`h-32 bg-gradient-to-br transition-colors duration-500 p-4 flex flex-col items-center justify-center text-white relative overflow-hidden ${
                            activeMarketingIndex === 0 ? 'from-red-500 to-orange-500' :
                            activeMarketingIndex === 1 ? 'from-purple-500 to-indigo-500' :
                            activeMarketingIndex === 2 ? 'from-orange-400 to-yellow-500' :
                            'from-blue-500 to-cyan-500'
                        }`}>
                            <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/20 rounded-full"></div>
                            <div className="text-4xl mb-2 animate-bounce">{marketingFeatures[activeMarketingIndex].icon}</div>
                            <div className="text-lg font-bold opacity-90">{marketingFeatures[activeMarketingIndex].title}</div>
                        </div>
                        {/* 中间锯齿 */}
                        <div className="h-4 bg-white relative -mt-2">
                             <div className="absolute top-0 left-0 w-full h-2 bg-[radial-gradient(circle,transparent_4px,#fff_4px)] bg-[length:12px_12px] -mt-1"></div>
                        </div>
                        {/* 底部内容 */}
                        <div className="flex-1 p-6 flex flex-col items-center justify-between">
                            <div className="text-center text-gray-500 text-sm leading-relaxed px-2">
                                {marketingFeatures[activeMarketingIndex].description}
                            </div>
                            <button className={`w-full py-2 rounded-full text-sm font-bold mt-4 transition-colors duration-300 ${marketingFeatures[activeMarketingIndex].color} hover:opacity-80`}>
                                立即体验
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </Section>
    </>
  );
};

export default HomePage;
